/* eslint-disable react/prop-types */
import { clsx } from "clsx";
import React, { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";

import { Meta } from "../blocks/Meta/Meta";
import { useLanguage } from "../i18n/useLanguage";
import { getDealerBasePath } from "../navigation/getDealerBasePath";
import { useDealerAccountInfo } from "../stores/hooks/useDealerAccountInfo";
import { useDealerAddress } from "../stores/hooks/useDealerAddress";
import { useDealerHours } from "../stores/hooks/useDealerHours";
import { Button } from "../ui/Button/Button";
import { KeyValue } from "../ui/KeyValue/KeyValue";
import { Section as Card } from "../ui/Section/Section";
import { ArrowLeftIcon } from "../ui/icons/ArrowLeftIcon";
import { EnvelopeFilledIcon } from "../ui/icons/EnvelopeFilledIcon";
import { LocationPinFilledIcon } from "../ui/icons/LocationPinFilledIcon";
import { PhoneIcon } from "../ui/icons/PhoneIcon";
import { formatWorkingHours } from "../util/formatWorkingHours";

type Entry = {
  icon: ReactNode;
  label: string;
  value: ReactNode;
};

export const DealerContactUsPage: FunctionComponent = () => {
  const { t } = useTranslation();
  const [language] = useLanguage();
  const { dealerAddress, dealerContactEmail, dealerContactPhone, dealerName } =
    useDealerAccountInfo();
  const address = useDealerAddress();
  const dealerHours = useDealerHours();
  const entries: Entry[] = [
    dealerContactPhone && {
      icon: <PhoneIcon />,
      label: t("contactUs.phone"),
      value: (
        <a
          className="underline"
          href={`tel:${dealerContactPhone.replace(/\D/g, "")}`}
        >
          {dealerContactPhone}
        </a>
      ),
    },
    dealerContactEmail && {
      icon: <EnvelopeFilledIcon />,
      label: t("contactUs.email"),
      value: (
        <a className="underline" href={`mailto:${dealerContactEmail}`}>
          {dealerContactEmail}
        </a>
      ),
    },
    dealerAddress && {
      icon: <LocationPinFilledIcon />,
      label: t("contactUs.address"),
      value: address,
    },
  ].filter(Boolean) as Entry[];
  return (
    <>
      <Meta cmsTitle={null} title="dealerContactUsPage" />
      <header className="sm:border-b border-edge mt-4 md:mt-10 sm:pb-2">
        <Link to={getDealerBasePath()}>
          <Button
            aria-label="homepageLink"
            className="-ml-4 text-ink-2 font-thin mb-2"
            element="a"
          >
            <ArrowLeftIcon />
            {t("contactUs.home")}
          </Button>
        </Link>
        <h2 className="text-2xl md:text-3xl font-semibold">
          {t("contactUs.header")}
        </h2>
      </header>
      <section className="flex flex-col gap-2 sm:gap-4 sm:flex-row sm:justify-center p-4 sm:p-8">
        {entries.map(({ icon, label, value }) => (
          <Card
            className={clsx(
              "flex flex-col items-center justify-center px-4 py-10 bg-paper text-ink-2 gap-y-1",
              "w-full max-w-[24rem]"
            )}
            key={label}
          >
            <div className="rounded-tw-full flex items-center justify-center bg-paper-2 p-4 text-primary">
              {icon}
            </div>
            <h3 className="text-lg font-bold mt-3">{label}</h3>
            <p className="font-light text-sm">{value}</p>
          </Card>
        ))}
      </section>
      <section className="mb-8 mx-4">
        <header className="text-center my-4">
          <h2 className="text-xl md:text-2xl">
            {t("contactUs.hoursHeader", { dealerName })}
          </h2>
          <h4 className="font-thin text-ink-2">
            {t("contactUs.hoursSubtitle")}
          </h4>
        </header>
        <Card className="bg-paper w-full max-w-[24rem] mx-auto md:p-8 text-xs md:space-y-4">
          {dealerHours.map(({ close, day, open }) => (
            <KeyValue className="justify-between" key={day[language]}>
              <>{day[language]}:</>
              <>
                {formatWorkingHours(open, close, language) ||
                  t("contactUs.closed")}
              </>
            </KeyValue>
          ))}
        </Card>
      </section>
    </>
  );
};
